import { useQuery } from '@tanstack/react-query';
import { CalendarDays } from 'lucide-react';
import WidgetShell from '@/components/admin/dashboard/WidgetShell';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';

/**
 * Next few upcoming events for the admin home dashboard.
 *
 * Mirrors GET /admin/events (default status=upcoming: starts_at >= now - 1 day,
 * ordered ascending). The list handler returns SELECT * rows, so only the
 * fields this widget renders are declared.
 */
interface AdminEvent {
  id: string;
  slug: string;
  title: string;
  event_type: string;
  starts_at: string;
  location_text: string | null;
}

interface EventsResponse {
  events: AdminEvent[];
}

export default function UpcomingEventsWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'events', 'upcoming'],
    queryFn: () => api.get<EventsResponse>('/admin/events?status=upcoming&limit=5'),
    staleTime: 60000,
  });

  const events = data?.events ?? [];

  return (
    <WidgetShell
      title="Upcoming Events"
      icon={<CalendarDays />}
      to="/admin/events"
      isLoading={isLoading}
      error={error}
      isEmpty={!!data && events.length === 0}
      emptyMessage="No upcoming events scheduled."
    >
      {data && events.length > 0 && (
        <ul className="divide-y divide-line">
          {events.slice(0, 5).map((event) => (
            <li key={event.id} className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0">
              <p className="min-w-0 truncate text-sm font-medium text-ink">{event.title}</p>
              <span className="shrink-0 text-xs text-stone-500">{formatDate(event.starts_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </WidgetShell>
  );
}
